// src/pages/AllProducts.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import apiClient from '../services/api'; 
import '../styles/AllProducts.css'; 

const AllProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState('');
  const [filterVendor, setFilterVendor] = useState('All');
  const [filterCategory, setFilterCategory] = useState('All');
  const [filterStock, setFilterStock] = useState('All');

  useEffect(() => { fetchAdminProducts(); }, []);

  const fetchAdminProducts = async () => {
    try {
      const response = await apiClient.get('/products/admin/all');
      setProducts(response.data.products);
    } catch (error) { console.error("Error fetching all products:", error); }
    finally { setLoading(false); }
  };
  
  // 🔥 SMART STOCK CALCULATOR 🔥
  const getProductStock = (p) => {
    if (p.hasVariants && p.variants && p.variants.length > 0) {
      return p.variants.reduce((acc, v) => acc + (Number(v.inventory?.stockQuantity) || 0), 0);
    }
    return Number(p.inventory?.stockQuantity) || 0;
  };

  const handleDelete = async (id, productName) => {
    if (!window.confirm(`Are you sure you want to delete "${productName}" forever?`)) return;
    try {
      await apiClient.delete(`/products/admin/${id}`);
      setProducts(products.filter(p => p._id !== id));
      alert("Product deleted successfully!");
    } catch (error) { alert("Failed to delete product."); }
  };

  const handleToggleStatus = async (id) => {
    try {
      const res = await apiClient.put(`/products/admin/toggle-status/${id}`);
      setProducts(products.map(p => p._id === id ? { ...p, isActive: res.data.isActive } : p));
    } catch (error) { alert("Failed to change product status."); }
  };

  const handleMarkOutOfStock = async (id, productName) => {
    if (!window.confirm(`Mark "${productName}" as Out of Stock?`)) return;
    try {
      await apiClient.put(`/products/admin/out-of-stock/${id}`);
      
      // 🔥 Update Local State for both Simple and Variants 🔥
      setProducts(products.map(p => {
        if (p._id === id) {
          return { 
            ...p, 
            inventory: { ...p.inventory, stockQuantity: 0 },
            variants: p.hasVariants ? p.variants.map(v => ({...v, inventory: {...v.inventory, stockQuantity: 0}})) : p.variants
          };
        }
        return p;
      }));
      
      alert("Product is now Out of Stock!");
    } catch (error) { alert("Failed to update stock status."); }
  };

  const vendors = useMemo(() => {
    const uniqueVendors = new Map();
    products.forEach(p => { if (p.vendorId && p.vendorId._id) uniqueVendors.set(p.vendorId._id, p.vendorId.shopName); });
    return Array.from(uniqueVendors, ([id, name]) => ({ id, name }));
  }, [products]);

  const categories = useMemo(() => Array.from(new Set(products.map(p => p.category))), [products]);

  // 🔥 UPDATED FILTER LOGIC 🔥
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesVendor = filterVendor === 'All' || (p.vendorId && p.vendorId._id === filterVendor);
      const matchesCategory = filterCategory === 'All' || p.category === filterCategory;
      
      const stock = getProductStock(p);
      let matchesStock = true;
      if (filterStock === 'InStock') matchesStock = stock > 0;
      if (filterStock === 'OutOfStock') matchesStock = stock <= 0;
      
      return matchesSearch && matchesVendor && matchesCategory && matchesStock;
    });
  }, [products, searchTerm, filterVendor, filterCategory, filterStock]);

  const getFullImageUrl = (url) => {
    if (!url) return '';
    return (url.startsWith('http://') || url.startsWith('https://')) ? url : `https://pickupdrop.dodigitals.in${url.startsWith('/') ? url : `/${url}`}`;
  };

  return (
    <div className="admin-products-container">
      <div className="admin-header">
        <div><h2 className="admin-title">All Products</h2><p className="admin-subtitle">Total {filteredProducts.length} products found.</p></div>
        <button className="btn-add-product" onClick={() => navigate('/dashboard/products/add')}>+ Add New Product</button>
      </div>

      <div className="filters-container">
        <input type="text" className="filter-input" placeholder="🔍 Search product..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        <select className="filter-select" value={filterVendor} onChange={(e) => setFilterVendor(e.target.value)}><option value="All">🏪 All Stores</option>{vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}</select>
        <select className="filter-select" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}><option value="All">📂 All Categories</option>{categories.map(cat => <option key={cat} value={cat}>{cat}</option>)}</select>
        <select className="filter-select" value={filterStock} onChange={(e) => setFilterStock(e.target.value)}><option value="All">📦 All Stock</option><option value="InStock">✅ In Stock</option><option value="OutOfStock">❌ Out of Stock</option></select>
      </div>

      <div className="table-wrapper">
        {loading ? <div style={{ padding: '50px', textAlign: 'center' }}>Loading...</div> : (
          <table className="admin-table">
            <thead><tr><th>Image</th><th>Details</th><th>Store</th><th>Price</th><th>Stock</th><th>Actions</th></tr></thead>
            <tbody>
              {filteredProducts.map((p) => {
                
                // 🔥 1. SMART IMAGE SELECTION 🔥
                let displayImage = p.images && p.images.length > 0 ? p.images[0] : null;
                if (!displayImage && p.hasVariants && p.variants && p.variants.length > 0) {
                  const varWithImg = p.variants.find(v => v.image || (v.images && v.images.length > 0));
                  if (varWithImg) displayImage = varWithImg.image || varWithImg.images[0];
                }

                // 🔥 2. SMART PRICE CALCULATION 🔥
                let sellPrice = 0;
                let hasMultiplePrices = false;
                if (p.hasVariants && p.variants && p.variants.length > 0) {
                  const prices = p.variants.map(v => Number(v.pricing?.sellingPrice) || 0);
                  sellPrice = Math.min(...prices);
                  hasMultiplePrices = sellPrice !== Math.max(...prices);
                } else {
                  sellPrice = p.pricing?.sellingPrice || 0;
                }

                // 🔥 3. SMART STOCK CALCULATION 🔥
                const stock = getProductStock(p);
                const inStock = stock > 0;

                return (
                  <tr key={p._id}>
                    <td>
                      {displayImage ? <img src={getFullImageUrl(displayImage)} alt="prod" className="td-img" /> : <div className="td-img" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '10px' }}>No Img</div>}
                    </td>
                    <td>
                      <div className="prod-name">{p.name}</div>
                      <div className="prod-cat">{p.category} {p.subCategory && `> ${p.subCategory}`}</div>
                    </td>
                    <td>
                      <div className="shop-name">{p.vendorId?.shopName || "Unknown"}</div>
                      <div style={{ fontSize: '12px', color: '#64748B' }}>{p.vendorId?.phone || "N/A"}</div>
                    </td>
                    <td>
                      <div className="price-sell">₹{sellPrice} {hasMultiplePrices && <span style={{fontSize: '12px', color: '#64748B', fontWeight: 'normal'}}>onwards</span>}</div>
                    </td>
                    <td>
                      <span className={`stock-badge ${inStock ? 'stock-in' : 'stock-out'}`}>
                        {inStock ? `${stock} in Stock` : 'Out of Stock'}
                      </span>
                      {p.hasVariants && <div style={{ fontSize: '10px', color: '#ea6724', marginTop: '4px', fontWeight: 'bold' }}>{p.variants.length} Variants</div>}
                    </td>
                    <td>
                      <div className="action-btns" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <button className="btn-edit" onClick={() => navigate(`/dashboard/products/edit/${p._id}`)}>✏️ Edit</button>
                        <button onClick={() => handleToggleStatus(p._id)} style={{ padding: '6px', cursor: 'pointer', background: p.isActive !== false ? '#E2E8F0' : '#FEF08A', border: 'none', borderRadius: '6px' }}>
                          {p.isActive !== false ? '👁️ Hide' : '👀 Show'}
                        </button>
                        {inStock && (
                          <button onClick={() => handleMarkOutOfStock(p._id, p.name)} style={{ padding: '6px', cursor: 'pointer', background: '#F8FAFC', border: '1px solid #CBD5E1', borderRadius: '6px', fontSize: '12px' }}>
                            📦 Out of Stock
                          </button>
                        )}
                        <button className="btn-delete" onClick={() => handleDelete(p._id, p.name)}>🗑️ Delete</button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AllProducts;